import { employeesNotDeleted, employeesDeleted, getOrganization, writeAccess } from 'src/store/selectors';
import type { State } from 'src/store/types.js';
import { LocalDate } from 'src/utilities/local-date.js';
import type { ApiClient } from 'src/store/backend';
import { getPromoDataForEntity } from 'src/store/selectors/promo';
import type { CommonDataEntityView } from '../../content/common-data-entity-view';
import type { TutorialView } from '../../content/tutorials/tutorial-view';
import { assertIsDefined } from '../../assertIsDefined';
import { assertEntityFound } from './entity-not-found';
import { getFeatureStates } from '../../store/selectors/features/getFeatureStates';
import { tutorialsForOrganization } from 'src/store/selectors/tutorials-for-organization';
import type { TutorialRegistration } from 'src/library/lists/tutorial-registration';

export function buildTutorialView(
  _api: ApiClient,
  uuid: string,
  commonData: CommonDataEntityView,
  state: State,
  currentParent: string,
): TutorialView {
  const organization = getOrganization(state);
  assertIsDefined(organization);

  const item = tutorialsForOrganization(organization).find((t) => t.id === uuid);
  assertEntityFound(item, 'tutorials', uuid);

  // Use registrations from tutorial (comes from API)
  // Filter and validate registrations to ensure they have required fields
  const registrations: {
    id: any;
    tutorialId: number;
    registrationDate: any;
    dueDate: string | null;
    contentVersionId: string;
    participants: any;
  }[] = (item.registrations || [])
    .filter((reg) => reg.id && reg.registrationDate && reg.participants)
    .map((reg) => ({
      id: reg.id!,
      tutorialId: reg.tutorialId || parseInt(uuid, 10),
      registrationDate: reg.registrationDate!,
      dueDate: reg.dueDate || null,
      contentVersionId: reg.contentVersionId || '',
      participants: reg.participants!,
    }));

  const registrationsCurrentVersion = registrations.filter((r) => {
    return r.contentVersionId === item.contentVersionId;
  });

  const registrationsOldVersions = registrations.filter((r) => {
    return r.contentVersionId !== item.contentVersionId;
  });

  // Find employees with active registrations (PENDING or IN_PROGRESS, and dueDate not yet passed)
  const today = LocalDate.fromString(state.today);
  const activeParticipantUuids = new Set<string>();
  registrations.forEach((reg) => {
    const dueDate = reg.dueDate ? LocalDate.fromString(reg.dueDate) : null;
    const isDueDatePassed = dueDate !== null && dueDate.isBefore(today);
    reg.participants.forEach((p) => {
      if (p.employeeUuid && (p.status === 'PENDING' || p.status === 'IN_PROGRESS') && !isDueDatePassed) {
        activeParticipantUuids.add(p.employeeUuid);
      }
    });
  });

  // Build employees list with access info (ACTIVE only, for registration dialog)
  const employees = employeesNotDeleted(state)
    .filter((e) => e.status === 'ACTIVE')
    .map((e) => {
      const hasActiveRegistration = activeParticipantUuids.has(e.uuid);
      return {
        id: crypto.randomUUID(), // Generate ID for potential registration
        uuid: e.uuid,
        employeeUuid: e.uuid,
        name: e.name,
        hasAccess: e.accessLevel !== 'NONE',
        completed: null,
        status: 'PENDING' as const,
        statusComment: null,
        hasActiveRegistration, // Mark if already registered
      };
    });

  // Build complete employee list (including inactive/terminated/deleted, for displaying participant names)
  const allEmployees = [...employeesNotDeleted(state), ...employeesDeleted(state)];
  const allEmployeesForNameLookup = allEmployees.reduce(
    (acc, e) => {
      acc[e.uuid] = { name: e.name, hasAccess: e.accessLevel !== 'NONE' };
      return acc;
    },
    {} as Record<string, { name: string; hasAccess: boolean }>,
  );

  // Sort registrations by date (newest first)
  // Add employee names to participants (API doesn't include names)
  function sortedRegistrationWithNames(registrations: TutorialRegistration[]): TutorialRegistration[] {
    return registrations
      .sort((a, b) => new Date(b.registrationDate).getTime() - new Date(a.registrationDate).getTime())
      .map((reg) => ({
        ...reg,
        participants: reg.participants.map((p) => {
          const employeeInfo = p.employeeUuid ? allEmployeesForNameLookup[p.employeeUuid] : undefined;
          return {
            id: p.id || '',
            employeeUuid: p.employeeUuid || null,
            completed: p.completed || null,
            status: p.status || 'PENDING',
            statusComment: p.statusComment || null,
            name: employeeInfo?.name,
            hasAccess: employeeInfo?.hasAccess,
          };
        }),
      }));
  }

  return {
    ...commonData,
    promoData: getPromoDataForEntity('tutorials', item, getFeatureStates(state)),
    contentVersionId: item.contentVersionId,
    docsForLinking: [],
    currentUserHasAccess: true,
    currentUserHasWriteAccess: writeAccess(state),
    attachments: [],
    guide: item.guide,
    helpContent: '',
    content: item.description,
    name: item.title,
    today: LocalDate.fromString(state.today),
    type: 'tutorials',
    uuid: uuid,
    isConfirmedEntity: true,
    href: currentParent + '/tutorials/' + uuid,
    pdfHref: '',
    parentHref: currentParent,
    deleted: false,
    employees,
    registrationsNew: sortedRegistrationWithNames(registrationsCurrentVersion),
    registrationsOld: sortedRegistrationWithNames(registrationsOldVersions),
    deletable: true,
    hasDraft: false,
    fetchDraft: () => Promise.reject(new Error('No draft')),
  };
}
